/**
 * useToast — Global Toast Notification Service
 * Singleton reactive store pattern. No Vuex required.
 * Usage: import { useToast } from '@/composables/useToast'
 *        const toast = useToast()
 *        toast.success('Invoice created successfully')
 *        toast.error('Failed to save. Try again.')
 */
import { reactive } from "vue";

let _uid = 0;

const state = reactive({
  toasts: [],
});

function addToast({ message, type = "info", duration = 4000, title = null }) {
  const id = ++_uid;
  const toast = { id, message, type, title, duration, visible: true };
  state.toasts.push(toast);

  if (duration > 0) {
    setTimeout(() => removeToast(id), duration);
  }
  return id;
}

function removeToast(id) {
  const idx = state.toasts.findIndex((t) => t.id === id);
  if (idx !== -1) state.toasts.splice(idx, 1);
}

const toastService = {
  /** Reactive toasts array – consumed by AppToastContainer */
  state,

  /**
   * @param {string} message
   * @param {{ title?: string, duration?: number }} [options]
   */
  success(message, options = {}) {
    return addToast({ message, type: "success", ...options });
  },

  /**
   * @param {string} message
   * @param {{ title?: string, duration?: number }} [options]
   */
  error(message, options = {}) {
    return addToast({ message, type: "error", duration: 6000, ...options });
  },

  /**
   * @param {string} message
   * @param {{ title?: string, duration?: number }} [options]
   */
  warning(message, options = {}) {
    return addToast({ message, type: "warning", ...options });
  },

  /**
   * @param {string} message
   * @param {{ title?: string, duration?: number }} [options]
   */
  info(message, options = {}) {
    return addToast({ message, type: "info", ...options });
  },

  /** Manually dismiss a toast by ID */
  dismiss(id) {
    removeToast(id);
  },

  /** Dismiss all active toasts */
  clear() {
    state.toasts.splice(0);
  },
};

/**
 * Composable entry point — returns the singleton service.
 * Identical reference every call, safe to use across components.
 */
export function useToast() {
  return toastService;
}

// Named export for direct usage without composable pattern
export default toastService;
